import { get, set, del } from "idb-keyval";
import type { ImageAttachment } from "@/components/ai/prompt-input/state";

export const supportsIndexedDB = typeof indexedDB !== "undefined";

/**
 * Shape stored in IndexedDB. Raw bytes and metadata only —
 * blob preview URLs are recreated on load.
 */
export type StoredImage = {
    id: string;
    data: Uint8Array;
    name: string;
    mimeType: string;
};

export function toStoredImage(img: ImageAttachment): StoredImage {
    return { id: img.id, data: img.data, name: img.name, mimeType: img.mimeType };
}

export function fromStoredImage(stored: StoredImage): ImageAttachment {
    // IndexedDB deserialization may widen the buffer type; ensure it's ArrayBuffer-backed.
    const data = new Uint8Array(stored.data);
    const blob = new Blob([data], { type: stored.mimeType });
    return {
        id: stored.id,
        data,
        preview: URL.createObjectURL(blob),
        name: stored.name,
        mimeType: stored.mimeType,
    };
}

export async function saveImagesToIDB(key: string, images: ImageAttachment[]): Promise<void> {
    if (!supportsIndexedDB) return;
    try {
        if (images.length === 0) {
            await del(key);
        } else {
            await set(key, images.map(toStoredImage));
        }
    } catch (e) {
        console.warn("Failed to save images to IndexedDB:", e);
    }
}

export async function loadImagesFromIDB(key: string): Promise<ImageAttachment[]> {
    if (!supportsIndexedDB) return [];
    try {
        const stored = await get<StoredImage[]>(key);
        if (!stored || stored.length === 0) return [];
        return stored.map(fromStoredImage);
    } catch (e) {
        console.warn("Failed to load images from IndexedDB:", e);
        return [];
    }
}

export async function clearImagesFromIDB(key: string): Promise<void> {
    if (!supportsIndexedDB) return;
    try {
        await del(key);
    } catch (e) {
        console.warn("Failed to clear images from IndexedDB:", e);
    }
}
